import React from 'react';
import { Snackbar, Alert, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useBilling } from '../../context/billingContextInstance';
import { goToLimitAction, limitActionOf, limitButton } from './limitAction';

/**
 * #1294 (P6) — el aviso ÚNICO de «algo se ha parado por saldo», el mismo en todas las
 * superficies: chat, planes, vectorización y subida de ficheros. Lo alimenta la señal
 * `billing:stop` (`dispatchCreditStop` en apiClient), que emiten los 402 de la puerta, la barra
 * de progreso de la vectorización y el chat al recibir un corte.
 *
 * Prueba o comp → «Elegir un plan» (/billing/required): no piden más uso (#759 D18). Pago → la
 * misma salida que el aviso de plan agotado (`limitActionOf`): subir al plan siguiente abre su
 * diálogo, y el resto (contactar, soporte, avisar al admin, pedir renovación) abre ese aviso.
 */
// Clave de traducción y texto en español (el de respaldo) de cada motivo de corte.
const STOP_TEXT = {
  credit_exhausted: ['billing.stop.creditExhausted', 'Se ha detenido por saldo agotado.'],
  debt_limit_reached: ['billing.stop.debtLimitReached', 'Se ha detenido: has superado el margen de consumo de tu plan.'],
  trial_expired: ['billing.stop.trialExpired', 'Tu prueba ha terminado.'],
  trial_doc_limit: ['billing.stop.trialDocLimit', 'Has usado todos los documentos de la prueba. Las cuentas de pago no tienen este límite.'],
  balance_unavailable: ['billing.stop.balanceUnavailable', 'Se ha detenido: no hemos podido comprobar tu saldo. Inténtalo en unos minutos.'],
  renewal_unpaid: ['billing.stop.renewalUnpaid', 'Se ha detenido: no hemos podido cobrar la renovación de tu plan.'],
};

const SURFACE_TEXT = {
  vectorization: ['billing.stop.surfaceVectorization', 'La vectorización se ha detenido por saldo: los documentos pendientes no se han procesado.'],
  chat: ['billing.stop.surfaceChat', 'La respuesta se ha detenido por saldo.'],
  upload: ['billing.stop.surfaceUpload', 'No se ha subido nada: no te queda saldo en tu plan.'],
};

/** El texto del corte en el idioma de la interfaz: `t` es el de `useTranslation`. */
export function creditStopMessage(notice, t) {
  if (!notice) return '';
  if (notice.reason === 'trial_doc_limit' && notice.docsMax) {
    return t('billing.stop.trialDocLimitMax', {
      max: notice.docsMax,
      defaultValue: 'La prueba incluye hasta {{max}} documentos y ya los has usado. Las cuentas de pago no tienen este límite.',
    });
  }
  const surfaceApplies = !['trial_doc_limit', 'trial_expired', 'balance_unavailable', 'renewal_unpaid'].includes(notice.reason);
  const [key, fallback] = (surfaceApplies && SURFACE_TEXT[notice.surface])
    || STOP_TEXT[notice.reason]
    || STOP_TEXT.credit_exhausted;
  return t(key, fallback);
}

const CreditStopNotice = () => {
  const { stopNotice, dismissStopNotice, isTrial: usageSaysTrial, openLimitNotice, usage } = useBilling();
  const navigate = useNavigate();
  const { t } = useTranslation();
  // Lo que diga el propio corte manda; el consumo cargado es el respaldo.
  const isTrial = typeof stopNotice?.isTrial === 'boolean' ? stopNotice.isTrial : usageSaysTrial;
  const choosesPlan = isTrial || Boolean(usage?.freeAccess);
  // #759 R: no es falta de saldo, es una renovación sin cobrar: su salida es pagarla.
  const renewalUnpaid = stopNotice?.reason === 'renewal_unpaid';
  const paidAction = limitActionOf(usage);

  const handleAction = () => {
    dismissStopNotice();
    if (renewalUnpaid) {
      navigate('/billing/renewal');
    } else if (choosesPlan || stopNotice?.reason === 'trial_expired' || stopNotice?.reason === 'trial_doc_limit') {
      navigate('/billing/required');
    } else {
      goToLimitAction(paidAction, { navigate, openLimitNotice });
    }
  };

  return (
    <Snackbar
      open={Boolean(stopNotice)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      onClose={(_, reason) => { if (reason !== 'clickaway') dismissStopNotice(); }}
    >
      <Alert
        severity="warning"
        variant="filled"
        onClose={dismissStopNotice}
        data-testid="credit-stop-notice"
        action={(
          <Button color="inherit" size="small" onClick={handleAction} sx={{ textTransform: 'none', fontWeight: 600 }}>
            {renewalUnpaid
              ? t('billing.renewal.goPay', 'Ver cómo pagar')
              : choosesPlan
                ? t('billing.freeAccess.choosePlan', 'Elegir un plan')
                : limitButton(t, paidAction)}
          </Button>
        )}
      >
        {creditStopMessage(stopNotice, t)}
      </Alert>
    </Snackbar>
  );
};

export default CreditStopNotice;
